import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./BlogDetails.css";
import api from "../../Api";
import Navber from "../../Components/Navber.jsx";

/* ============================================================
   HELPERS
   ============================================================ */

// Matches http(s)://... OR www....
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

// Characters we don't want to include at the very end of a URL
const TRAILING_PUNCT_REGEX = /[.,;:!?)\]}'"]+$/;

const splitTrailingPunctuation = (url) => {
  const m = url.match(TRAILING_PUNCT_REGEX);
  if (!m) return [url, ""];
  const trailing = m[0];
  return [url.slice(0, -trailing.length), trailing];
};

/**
 * Prepares description HTML for rendering:
 *  1) Existing <a> tags -> force target="_blank" + rel="noopener noreferrer"
 *  2) Plain-text URLs    -> wrapped in <a target="_blank" ...>
 */
const prepareDescriptionHtml = (html) => {
  if (!html) return "";

  let doc;
  try {
    doc = new DOMParser().parseFromString(String(html), "text/html");
  } catch {
    return String(html);
  }

  /* ---------- 1) Fix existing <a> tags ---------- */
  doc.body.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
    a.classList.add("jayraj-blogdetail-link");
  });

  /* ---------- 2) Linkify plain-text URLs ---------- */
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodesToProcess = [];
  let node;

  while ((node = walker.nextNode())) {
    // Skip text that is already inside an <a>
    if (node.parentElement?.closest("a")) continue;

    URL_REGEX.lastIndex = 0;
    if (URL_REGEX.test(node.nodeValue)) {
      textNodesToProcess.push(node);
    }
    URL_REGEX.lastIndex = 0;
  }

  textNodesToProcess.forEach((textNode) => {
    const text = textNode.nodeValue;
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match;

    URL_REGEX.lastIndex = 0;
    while ((match = URL_REGEX.exec(text)) !== null) {
      const rawUrl = match[0];
      const start = match.index;
      const [cleanUrl, trailingPunct] = splitTrailingPunctuation(rawUrl);

      // Text before the URL
      if (start > lastIndex) {
        fragment.appendChild(
          doc.createTextNode(text.slice(lastIndex, start))
        );
      }

      // The <a> element
      const a = doc.createElement("a");
      a.setAttribute(
        "href",
        cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl
      );
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      a.className = "jayraj-blogdetail-link";
      a.textContent = cleanUrl;
      fragment.appendChild(a);

      // Any trailing punctuation after the URL
      if (trailingPunct) {
        fragment.appendChild(doc.createTextNode(trailingPunct));
      }

      lastIndex = start + rawUrl.length;
    }

    // Remaining text after the last URL
    if (lastIndex < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
  });

  return doc.body.innerHTML;
};

/* ============================================================
   COMPONENT
   ============================================================ */

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  /* ---------------- LIGHTBOX STATE ---------------- */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  /* ---------------- FETCH DETAIL BY SLUG ---------------- */
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/happy-customers/slug/${slug}`);
        if (res.data?.status === true && res.data.data) {
          setPost(res.data.data);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error("Error fetching post detail:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchPost();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  /* ---------------- PREPARED DESCRIPTION HTML ---------------- */
  const descriptionHtml = useMemo(
    () => prepareDescriptionHtml(post?.description),
    [post?.description]
  );

  /* ---------------- LIGHTBOX HANDLERS ---------------- */
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  };

  const showPrev = useCallback(() => {
    if (!post?.images?.length) return;
    setLightboxIndex((prev) =>
      prev === 0 ? post.images.length - 1 : prev - 1
    );
  }, [post]);

  const showNext = useCallback(() => {
    if (!post?.images?.length) return;
    setLightboxIndex((prev) =>
      prev === post.images.length - 1 ? 0 : prev + 1
    );
  }, [post]);

  /* ---------------- KEYBOARD NAVIGATION ---------------- */
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxOpen, showPrev, showNext]);

  /* ---------------- CLEANUP ON UNMOUNT ---------------- */
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  /* ---------------- LOADING ---------------- */
  if (loading) {
    return (
      <div className="jayraj-blogdetail-page">
        <Navber />
        <div className="jayraj-blogdetail-notfound">
          <h2>Loading story...</h2>
        </div>
      </div>
    );
  }

  /* ---------------- NOT FOUND ---------------- */
  if (notFound || !post) {
    return (
      <div className="jayraj-blogdetail-page">
        <Navber />
        <div className="jayraj-blogdetail-notfound">
          <h2>Post not found</h2>
          <p>The article you're looking for doesn't exist.</p>
          <button
            className="jayraj-blogdetail-back-btn"
            onClick={() => navigate("/blogs")}
          >
            ← Back to Blogs
          </button>
        </div>
      </div>
    );
  }

  /* ---------------- RENDER ---------------- */
  const heroImage =
    post.images?.[0]?.url ||
    "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1600&q=80";

  return (
    <div className="jayraj-blogdetail-page">
      <Navber />

      {/* ================= HERO ================= */}
      <section className="jayraj-blogdetail-hero">
        <div className="jayraj-blogdetail-hero-bg">
          <img src={heroImage} alt={post.title} />
          <div className="jayraj-blogdetail-hero-overlay" />
        </div>

        <div className="jayraj-blogdetail-hero-content">
          <button
            className="jayraj-blogdetail-back"
            onClick={() => navigate("/blogs")}
          >
            ← Back to Blogs
          </button>

          <span className="jayraj-blogdetail-category">Happy Customer</span>
          <h1 className="jayraj-blogdetail-title">{post.title}</h1>

          <div className="jayraj-blogdetail-meta">
            <span>Jayraj Tours</span>
            <span className="jayraj-blogdetail-dot">•</span>
            <span>
              {new Date(post.created_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span className="jayraj-blogdetail-dot">•</span>
            <span>
              {post.images?.length || 0} photos
              {post.videos?.length
                ? ` · ${post.videos.length} videos`
                : ""}
            </span>
          </div>
        </div>
      </section>

      {/* ================= DESCRIPTION ================= */}
      {post.description && (
        <article className="jayraj-blogdetail-article">
          <div
            className="jayraj-blogdetail-content"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </article>
      )}

      {/* ================= PHOTO GALLERY ================= */}
      {post.images?.length > 0 && (
        <section className="jayraj-blogdetail-gallery">
          <h2 className="jayraj-blogdetail-related-title">Photo Gallery</h2>
          <div className="jayraj-blogdetail-gallery-grid">
            {post.images.map((img, index) => (
              <div
                className="jayraj-blogdetail-gallery-item"
                key={img.id}
                onClick={() => openLightbox(index)}
              >
                <img
                  src={img.url}
                  alt={`${post.title} - ${index + 1}`}
                  loading="lazy"
                />
                <span className="jayraj-blogdetail-gallery-zoom">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ================= VIDEO GALLERY ================= */}
      {post.videos?.length > 0 && (
        <section className="jayraj-blogdetail-gallery">
          <h2 className="jayraj-blogdetail-related-title">Videos</h2>
          <div className="jayraj-blogdetail-video-grid">
            {post.videos.map((vid) => (
              <div className="jayraj-blogdetail-video-wrap" key={vid.id}>
                <video
                  controls
                  className="jayraj-blogdetail-video"
                  preload="metadata"
                >
                  <source src={vid.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ================= LIGHTBOX ================= */}
      {lightboxOpen && post?.images?.length > 0 && (
        <div
          className="jayraj-lightbox-overlay"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="jayraj-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ✕
          </button>

          {post.images.length > 1 && (
            <button
              className="jayraj-lightbox-nav jayraj-lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
          )}

          <div
            className="jayraj-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={post.images[lightboxIndex]?.url}
              alt={`${post.title} - ${lightboxIndex + 1}`}
              className="jayraj-lightbox-img"
            />
            <div className="jayraj-lightbox-counter">
              {lightboxIndex + 1} / {post.images.length}
            </div>
          </div>

          {post.images.length > 1 && (
            <button
              className="jayraj-lightbox-nav jayraj-lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              aria-label="Next image"
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogDetail;